import {
  useQueryPlans,
  useQueryAccountPlan,
  useAllSalesFinalTreatment,
} from '../../../hooks';
import {
  useQueryAccount,
  useQuerySession,
  useQueryAccountUsage,
  useQueryEntitlements,
} from '../../../../universal-nav/hooks';
import { getNextPlan } from './getNextPlan';

import type { CurrentAccount, Plan } from '../../../../types';
import type { ZapierSession } from '../../../../types';

export type ResponseData = {
  isAuthenticated: boolean;
  currentUser: ZapierSession;
  currentAccount: CurrentAccount;
  possiblePlans: Array<{
    planType: string;
    plans: Array<Plan>;
  }>;
  nextPlan?: Plan;
  nextTeamPlan?: Plan;
  nextCompanyPlan?: Plan;
  allSalesFinal: string;
};

type AlertBannerResult =
  | AlertBannerLoadingResult
  | AlertBannerErrorResult
  | AlertBannerSuccessResult
  | AlertBannerNoDataResult;

type AlertBannerLoadingResult = {
  data: undefined;
  isLoading: true;
  isError: false;
};

type AlertBannerErrorResult = {
  data: undefined;
  isLoading: false;
  isError: true;
};

type AlertBannerSuccessResult = {
  data: ResponseData;
  isLoading: false;
  isError: false;
};

type AlertBannerNoDataResult = {
  data: undefined;
  isLoading: false;
  isError: false;
};

export const useAlertBannerData = (): AlertBannerResult => {
  const {
    data: sessionData,
    isLoading: isLoadingSessionData,
    isError: isErrorSessionData,
  } = useQuerySession();
  const {
    data: accountData,
    isLoading: isLoadingAccountData,
    isError: isErrorAccountData,
  } = useQueryAccount({ currentAccountId: sessionData?.currentAccountId });
  const {
    data: planGroupsData,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
  } = useQueryPlans(sessionData?.currentAccountId);
  const {
    data: accountUsageData,
    isLoading: isLoadingAccountUsageData,
    isError: isErrorAccountUsageData,
  } = useQueryAccountUsage(sessionData?.currentAccountId);
  const {
    data: accountPlanData,
    isLoading: isLoadingAccountPlanData,
    isError: isErrorAccountPlanData,
  } = useQueryAccountPlan(sessionData?.currentAccountId, accountData?.plan?.id);
  const {
    data: entitlementsData,
    isLoading: isLoadingEntitlementsData,
    isError: isErrorEntitlementsData,
  } = useQueryEntitlements(sessionData?.currentAccountId);
  const {
    treatment: allSalesFinalData,
    isLoading: isLoadingAllSalesFinalData,
  } = useAllSalesFinalTreatment(sessionData?.currentAccountId);

  let currentPlan;

  for (const group of Array.from(planGroupsData || [])) {
    const matchingPlan = group.plans.find(
      (plan) => plan.id === accountData?.plan.id
    );
    if (matchingPlan) {
      currentPlan = matchingPlan;
      break;
    }
  }

  const isLoading =
    isLoadingAccountData ||
    isLoadingSessionData ||
    isLoadingPlans ||
    isLoadingAccountUsageData ||
    isLoadingAccountPlanData ||
    isLoadingEntitlementsData ||
    isLoadingAllSalesFinalData;

  const isError =
    isErrorAccountData ||
    isErrorSessionData ||
    isErrorPlans ||
    isErrorAccountUsageData ||
    isErrorAccountPlanData ||
    isErrorEntitlementsData;

  const isAllDataAvailable =
    !!sessionData &&
    !!accountData &&
    !!planGroupsData &&
    !!accountUsageData &&
    !!currentPlan &&
    !!accountPlanData &&
    !!entitlementsData &&
    !!allSalesFinalData;

  if (isLoading) {
    return {
      isError: false,
      isLoading: true,
      data: undefined,
    };
  }

  if (isError) {
    return {
      isError: true,
      isLoading: false,
      data: undefined,
    };
  }

  if (!isAllDataAvailable) {
    return {
      isError: false,
      isLoading: false,
      data: undefined,
    };
  }

  const currentPlanSpecifics = {
    actions_used: accountUsageData.tasks.total,
    interval: accountPlanData.interval,
    plan_type: accountPlanData.planType,
    total_actions: accountPlanData.actions,
    included_tasks_per_month: accountPlanData.includedTasksPerMonth,
  };

  const alertBannerData: ResponseData = {
    isAuthenticated: sessionData.isLoggedIn,
    currentUser: sessionData,
    currentAccount: {
      id: accountData.id,
      currency: accountData.currency,
      currentUserRole: sessionData.currentUserRole,
      firstKnownUpgradeDate: accountData.firstKnownUpgradeDate,
      isUnlimited: accountData.isUnlimited,
      hasOverdueInvoice: accountData.hasOverdueInvoice,
      periodStartDate: accountUsageData.periodStart,
      periodEndDate: accountUsageData.periodEnd,
      isTrialing: accountData.isTrialing,
      trialEndDate: accountData.trialEndDate,
      isTeamAccount: accountData.isTeamAccount,
      isTrialingTeams: accountData.isTrialingTeams,
      teamAccountEndDate: accountData.teamAccountEndDate,
      teamsTrialAutoUpgrade: accountData.teamsTrialAutoUpgrade,
      teamTrialEndDate: accountData.teamTrialEndDate,
      isCompanyAccount: accountData.isCompanyAccount,
      isTrialingCompanies: accountData.isTrialingCompanies,
      companiesTrialEndDate: accountData.companiesTrialEndDate,
      companiesTrialAutoUpgrade: accountData.companiesTrialAutoUpgrade,
      isEnterpriseAccount: accountData.isEnterpriseAccount,
      isTrialingEnterprise: accountData.isTrialingEnterprise,
      enterpriseTrialEndDate: accountData.enterpriseTrialEndDate,
      taskBufferOverride: accountData.taskBufferOverride,
      entitlements: entitlementsData,
      plan: currentPlan!,
      paysManually: accountData.paysManually,
      subscriptionRenewalDate: accountData.subscriptionRenewalDate,
    },
    possiblePlans: planGroupsData,
    nextPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanInterval: 'year',
    }),
    nextTeamPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanType: 'Team',
    }),
    nextCompanyPlan: getNextPlan(planGroupsData, currentPlanSpecifics, {
      nextPlanInterval: 'year',
      nextPlanType: 'Company',
    }),
    allSalesFinal: allSalesFinalData,
  };

  return {
    data: alertBannerData,
    isLoading: false,
    isError: false,
  };
};
